import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { MemberNameLink } from '@/components/shared/member-name-link';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { usePreferences } from '@/stores/preferences-store';
import { formatDate, formatDateTime, formatMoney } from '@/utils/format';

export interface ContributionDetail {
  id: string;
  fundId: string;
  contributionType: string;
  memberId: string | null;
  giverName: string | null;
  identityHidden: boolean;
  amountMinor: number;
  currency: string;
  contributionDate: string;
  method: string;
  reference: string | null;
  note: string | null;
  receiptNumber: number;
  isVoided: boolean;
  voidReason: string | null;
  voidedByName: string | null;
  voidedAt: string | null;
  createdAt: string;
}

interface FundSummary {
  id: string;
  name: string;
}

const DetailRow = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="grid gap-1 border-b border-border py-3 last:border-b-0 sm:grid-cols-[9rem_1fr] sm:gap-4">
    <dt className="text-body-sm text-fg-secondary">{label}</dt>
    <dd className="text-body text-fg">{children}</dd>
  </div>
);

export const ContributionDetailBody = ({
  contributionId,
  linkOut = true,
  showIntro = true,
}: {
  contributionId: string;
  /** When false, fund/person names are plain text (dialog stays put). */
  linkOut?: boolean;
  /** Receipt title and one-line summary above the fields. */
  showIntro?: boolean;
}) => {
  const { t } = useTranslation('giving');
  const { t: tCommon } = useTranslation();
  const locale = usePreferences((state) => state.locale);
  const timezone = usePreferences((state) => state.timezone);

  const detail = useQuery({
    queryKey: ['giving', 'contributions', contributionId],
    queryFn: () => api.get<ContributionDetail>(`/giving/contributions/${contributionId}`),
    enabled: Boolean(contributionId),
  });

  const fund = useQuery({
    queryKey: ['giving', 'funds', detail.data?.fundId],
    queryFn: () => api.get<FundSummary>(`/giving/funds/${detail.data!.fundId}`),
    enabled: Boolean(detail.data?.fundId),
  });

  if (detail.isLoading) {
    return (
      <div className="space-y-3" aria-busy>
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  const row = detail.data;
  if (!row) {
    return (
      <p className="text-body-sm text-fg-secondary">{t('contributions.detail.notFoundBody')}</p>
    );
  }

  const giftDate = formatDate(row.contributionDate, { locale, style: 'medium' });

  const fundName = fund.data?.name;
  const fundNode = fundName ? (
    linkOut ? (
      <Link
        to={`/giving/funds/${row.fundId}`}
        className="font-medium text-brand hover:underline"
      >
        {fundName}
      </Link>
    ) : (
      fundName
    )
  ) : (
    tCommon('state.loading')
  );

  const giverNode = row.identityHidden
    ? t('contributions.identityHidden')
    : row.giverName
      ? linkOut && row.memberId
        ? (
            <MemberNameLink
              memberId={row.memberId}
              name={row.giverName}
              className="font-medium"
            />
          )
        : row.giverName
      : t('contributions.detail.anonymous');

  return (
    <div className="space-y-3">
      {showIntro ? (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-body font-medium text-fg">
              {t('contributions.receiptDated', {
                number: row.receiptNumber,
                date: giftDate,
              })}
            </p>
            {row.isVoided ? <Badge variant="danger">{t('contributions.voided')}</Badge> : null}
          </div>
          {fundName ? (
            <p className="text-body-sm text-fg-secondary">
              {t('contributions.detail.description', {
                amount: formatMoney(row.amountMinor, row.currency, { locale }),
                fund: fundName,
                date: giftDate,
              })}
            </p>
          ) : null}
        </>
      ) : null}

      <dl>
        <DetailRow label={t('contributions.columns.date')}>{giftDate}</DetailRow>
        <DetailRow label={t('contributions.columns.amount')}>
          {formatMoney(row.amountMinor, row.currency, { locale })}
        </DetailRow>
        <DetailRow label={t('contributions.columns.fund')}>{fundNode}</DetailRow>
        <DetailRow label={t('contributions.detail.giftType')}>
          {t(`kinds.${row.contributionType}`, { defaultValue: row.contributionType })}
        </DetailRow>
        <DetailRow label={t('contributions.columns.method')}>
          {t(`methods.${row.method}`, { defaultValue: row.method })}
        </DetailRow>
        <DetailRow label={t('contributions.detail.giver')}>{giverNode}</DetailRow>
        <DetailRow label={t('contributions.detail.recorded')}>
          {formatDateTime(row.createdAt, { locale, timezone, withZone: true })}
        </DetailRow>
        {row.reference ? (
          <DetailRow label={t('contributions.detail.reference')}>{row.reference}</DetailRow>
        ) : null}
        {row.note ? (
          <DetailRow label={t('contributions.detail.note')}>{row.note}</DetailRow>
        ) : null}
        {row.isVoided ? (
          <>
            {row.voidedAt ? (
              <DetailRow label={t('contributions.detail.voidedAt')}>
                {formatDateTime(row.voidedAt, { locale, timezone, withZone: true })}
              </DetailRow>
            ) : null}
            {row.voidedByName ? (
              <DetailRow label={t('contributions.detail.voidedBy')}>
                {row.voidedByName}
              </DetailRow>
            ) : null}
            <DetailRow label={t('contributions.viewReason')}>
              {row.voidReason?.trim() || t('contributions.voidReasonEmpty')}
            </DetailRow>
          </>
        ) : null}
      </dl>
    </div>
  );
};
